import type { Icon } from '@onefootprint/icons';
import { IcoEmail16, IcoFaceid16, IcoSmartphone16 } from '@onefootprint/icons';
import type { ChallengeKind, IdentifiedUser } from '@onefootprint/request-types';
import type { TFunction } from 'i18next';
import type { JSX } from 'react';
import type { DeviceInfo } from '@/idv/hooks';
import type { DIMetadata } from '@/idv/types';
import { IdentifyVariant } from '../../../../identify.types';
import { getDisplayEmail, getDisplayPhone } from '../../../../utils/get-display-contact-info';
type T = TFunction<'identify'>;
type TitleMap = Record<AvailableChallengeKinds, string | JSX.Element>;
const challengeIcons: Record<ChallengeKind, Icon> = {
  sna: IcoSmartphone16,
  sms: IcoSmartphone16,
  email: IcoEmail16,
  biometric: IcoFaceid16,
  sms_link: IcoSmartphone16
};
const challengePriority: Record<ChallengeKind, number> = {
  biometric: 0,
  sms: 1,
  email: 2,
  sms_link: 3,
  sna: 4
};
const EXCLUDE_CHALLENGE_KINDS = ['sna', 'sms_link'] as const;
type ExcludedChallengeKinds = (typeof EXCLUDE_CHALLENGE_KINDS)[number];
type AvailableChallengeKinds = Exclude<ChallengeKind, ExcludedChallengeKinds>;
const sortChallenges = (a: ChallengeKind, b: ChallengeKind) => challengePriority[a] - challengePriority[b];
const isAvailableChallengeKind = (kind: ChallengeKind): kind is AvailableChallengeKinds => !EXCLUDE_CHALLENGE_KINDS.includes(kind as ExcludedChallengeKinds);
export const getSubTitle = (t: T, variant: IdentifyVariant): string => variant !== IdentifyVariant.updateLoginMethods ? t($ => $['challenge-select-or-biometric']['log-in-options']) : t($ => $['challenge-select-or-biometric']['log-in-to-modify-details']);
export const getAvailableMethods = (user: IdentifiedUser, device: DeviceInfo): AvailableChallengeKinds[] => {
  const {
    type,
    hasSupportForWebauthn
  } = device;
  const kinds = [...(user?.availableChallengeKinds || [])];
  return kinds.sort(sortChallenges).filter(kind => kind !== 'biometric' || (type === 'mobile' ? hasSupportForWebauthn : hasSupportForWebauthn && user?.hasSyncablePasskey)).flatMap(kind => isAvailableChallengeKind(kind) ? [kind] : []);
};
export const getMethods = (user: IdentifiedUser, device: DeviceInfo, titleMap: TitleMap) => {
  return getAvailableMethods(user, device).map(kind => ({
    IconComponent: challengeIcons[kind],
    title: titleMap[kind],
    value: kind
  }));
};
function getChallengeTitleEmail(t: T, context: Context): string | JSX.Element {
  const displayEmail = getDisplayEmail({
    user: context.user,
    email: context.email?.value
  });
  const sendTo = t($ => $['challenge-select-or-biometric']['send-code-to']);
  return displayEmail ? <>
      {sendTo} <span data-dd-privacy="mask">{displayEmail}</span>
    </> : t($ => $['challenge-select-or-biometric']['send-code-via-email']);
}
const getChallengeTitlePhone = (t: T, context: Context): string | JSX.Element => {
  const displayPhone = getDisplayPhone({
    user: context.user,
    phoneNumber: context.phoneNumber?.value
  });
  const sendTo = t($ => $['challenge-select-or-biometric']['send-code-to']);
  return displayPhone ? <>
      {sendTo} <span data-dd-privacy="mask">{displayPhone}</span>
    </> : t($ => $['challenge-select-or-biometric']['send-code-via-sms']);
};
type Context = {
  user?: IdentifiedUser;
  email?: DIMetadata<string>;
  phoneNumber?: DIMetadata<string>;
};
export const getChallengeTitleByKind = (t: T, context: Context): TitleMap => ({
  sms: getChallengeTitlePhone(t, context),
  email: getChallengeTitleEmail(t, context),
  biometric: t($ => $['challenge-select-or-biometric'].passkey)
});